/**
 * Field-descriptor model for the manifest/catalog editor (docs/343-ADMIN-MANIFEST-
 * BUILDER-REDESIGN.md v0 §2). A descriptor is PRESENTATION ONLY (label/help/control
 * kind/options) — validity is decided by the real `@equipped/core` parsers
 * (`parseManifest`/`parseCatalog`), never by this file. Coverage ("no field
 * missed") is enforced by `satisfies Record<FieldKey, FieldDescriptor>` at every
 * registry's own definition site (`catalogFields.ts`/`packFields.ts`/
 * `lessonFields.ts`/`catalogPackEntryFields.ts`/`catalogNodeEntryFields.ts`) —
 * `npm run typecheck -w @equipped/admin-web-mantis` fails if a registry drops or
 * misses a key.
 */

/**
 * How `FieldForm` renders + edits one field. Every kind here has a matching case in
 * `FieldForm.tsx`.
 */
export type FieldControlKind =
  | "text"
  | "textarea"
  | "number"
  | "boolean3" // tri-state: absent ("use default") / true / false
  | "select"
  | "stringArray" // comma-separated chips (tags, languages, lesson-level `related`)
  | "recordBoolean" // open key → boolean rows (`platforms`)
  | "featureFlags" // the 8 fixed `Features` keys, each boolean3
  | "narrationTracks" // `NarrationTrack[]`
  | "facets" // `CatalogFacet[]`
  | "relatedRefs" // node-level `related` — `{id, title?}` rows
  | "countLabels" // `{catalog?:{one,other}, pack?:{one,other}}`
  | "catalogTerms" // `{url?, text?, version, title?, required?}`
  | "sectionsFrom" // pack `sections.from` — `"headings"` | `{linePrefix,suffix?,title?}` rows
  | "readonly"; // display-only (e.g. `raw` on an `type:"unknown"` lesson)

export interface FieldOption {
  value: string;
  label: string;
}

export interface FieldDescriptor {
  /** Section grouping shown above a run of fields in the panel (purely presentational). */
  section: string;
  label: string;
  kind: FieldControlKind;
  help?: string;
  /** For `kind: "select"`. */
  options?: FieldOption[];
  /** For `kind: "text"` — a "Browse…" button opens the R2 file picker for this field
   *  (v0: only the Lesson `file` field sets this, docs/343 §9). */
  filePicker?: boolean;
  /** For `kind: "number"` — whole numbers only (e.g. `level`). */
  integer?: boolean;
}

/** A ready-to-render field: its key + descriptor, in the order the panel shows it. */
export interface RenderField {
  key: string;
  descriptor: FieldDescriptor;
}

/** `descriptors` order-preserved into a flat render list — the one function every panel calls. */
export function renderFields<K extends string>(descriptors: Record<K, FieldDescriptor>): RenderField[] {
  return (Object.keys(descriptors) as K[]).map((key) => ({ key, descriptor: descriptors[key] }));
}

/** Plain read of a possibly-absent field off a loosely-typed draft object. */
export function fieldValue(draft: Record<string, unknown>, key: string): unknown {
  return draft[key];
}

/** Immutable field set — returns a NEW draft object with `key` set to `value` (or
 *  removed when `value === undefined`, so a cleared optional field round-trips to
 *  "absent," not `null`/`""`, matching how the Zod schemas treat it). */
export function withField(
  draft: Record<string, unknown>,
  key: string,
  value: unknown,
): Record<string, unknown> {
  const next = { ...draft };
  if (value === undefined) delete next[key];
  else next[key] = value;
  return next;
}
